/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useMemo } from "react";
import AddProduct from "../../../components/admin/add-product/AddProduct";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CustomButton from "../../../components/common/Button/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import AddProductContext from "../../../context/addProduct/AddProductContext";
import {
  Avatar,
  Snackbar,
  Typography,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Drawer,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import AllProductsContext from "../../../context/allProducts/AllProductsContext";
import { styles } from "./styles";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import AdminStatCard from "../../../components/admin/stat-card/AdminStatCard";
import ImageIcon from "@mui/icons-material/Image";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AllCategoriesContext from "../../../context/allCategories/AllCategoriesContext";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";

// Enhanced Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import TuneIcon from "@mui/icons-material/Tune";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatINR = (val) => {
  const num = Number(val);
  if (isNaN(num)) return "₹0";
  return "₹" + num.toLocaleString("en-IN");
};

const ProductsListAdmin = () => {
  const productAPI = useMemo(() => new ProductsAPI(), []);
  const allProductsContext = useContext(AllProductsContext);
  const allCategoriesContext = useContext(AllCategoriesContext);
  const productContext = useContext(AddProductContext);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [stockFilter, setStockFilter] = useState("ALL"); // ALL, IN_STOCK, LOW_STOCK, OUT_OF_STOCK
  const [visibilityFilter, setVisibilityFilter] = useState("ALL"); // ALL, VISIBLE, HIDDEN
  const [isRefreshing, setIsRefreshing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Quick View modal state
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  // Delete confirmation dialog state
  const [deleteDialogProduct, setDeleteDialogProduct] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const products = Array.isArray(allProductsContext.state)
    ? allProductsContext.state
    : [];

  const categories = Array.isArray(allCategoriesContext.state)
    ? allCategoriesContext.state
    : [];

  // Metrics
  const visibleProductsCount = products.filter((p) => p.visible !== false).length;
  const hiddenProductsCount = products.length - visibleProductsCount;
  const outOfStockCount = products.filter((p) => Number(p.unitsInStock) === 0).length;
  const lowStockCount = products.filter(
    (p) => Number(p.unitsInStock) > 0 && Number(p.unitsInStock) <= 5
  ).length;
  const healthyStockCount = products.filter((p) => Number(p.unitsInStock) > 5).length;

  // Filtered products calculation
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // 1. Text search
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matches = [
          product.title,
          product.id,
          product.searchTerms,
          product.unitType,
          product.parentCategory?.name,
          ...(product.categories || []),
          ...(product.properties || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

        if (!matches) return false;
      }

      // 2. Category filter
      if (selectedCategory !== "ALL") {
        const matchesCat =
          product.parentCategory?.id === selectedCategory ||
          product.parentCategory?.name === selectedCategory ||
          (product.categories || []).includes(selectedCategory);
        if (!matchesCat) return false;
      }

      // 3. Stock filter
      const units = Number(product.unitsInStock) || 0;
      if (stockFilter === "OUT_OF_STOCK" && units !== 0) return false;
      if (stockFilter === "LOW_STOCK" && (units === 0 || units > 5)) return false;
      if (stockFilter === "IN_STOCK" && units <= 5) return false;

      // 4. Visibility filter
      const isVisible = product.visible !== false;
      if (visibilityFilter === "VISIBLE" && !isVisible) return false;
      if (visibilityFilter === "HIDDEN" && isVisible) return false;

      return true;
    });
  }, [products, searchQuery, selectedCategory, stockFilter, visibilityFilter]);

  const initState = {
    categories: [],
    parentCategory: { id: "NONE", name: "None" },
    properties: [],
    id: "",
    title: "",
    searchTerms: "",
    description: "",
    maxPrice: "",
    listPrice: "",
    unitsInStock: "",
    netQuantity: "",
    unitType: "",
    images: [],
    visible: true,
    createdAt: null,
  };

  // Fetch all products and categories
  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      const resProducts = await productAPI.getProducts();
      if (resProducts && resProducts.data) {
        allProductsContext.setState(resProducts.data);
      }
      const resCategories = await productAPI.getCategories();
      if (resCategories && resCategories.data) {
        allCategoriesContext.setState(resCategories.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // For Add Product Modal
  const [openProductModal, setOpenProductModal] = useState(false);
  const [editProductModal, setEditProductModal] = useState(false);

  const showAddProductModal = (action) => {
    if (action === "ADD") {
      setEditProductModal(false);
      productContext.setState(initState);
    }
    setOpenProductModal(true);
  };

  const hideAddProductModal = () => {
    setOpenProductModal(false);
  };

  // Snackbar notifications
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });
  const showSnackbar = (text, type = "success") => {
    setMessage({ text, type });
    setSnackbarState(true);
  };
  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  // Edit product
  const setEditProduct = (prod) => {
    setEditProductModal(true);
    productContext.setState(prod);
    setOpenProductModal(true);
  };

  // Delete product confirmation
  const confirmDeleteProduct = (prod) => {
    setDeleteDialogProduct(prod);
  };

  const handleExecuteDelete = async () => {
    if (!deleteDialogProduct) return;
    setIsDeleting(true);
    try {
      const res = await productAPI.deleteProduct(deleteDialogProduct.id);
      if (res.success) {
        showSnackbar(`Product "${deleteDialogProduct.title}" deleted successfully.`, "success");
        const updated = allProductsContext.state.filter(
          (p) => p.id !== deleteDialogProduct.id
        );
        allProductsContext.setState(updated);
        setDeleteDialogProduct(null);
        if (quickViewProduct?.id === deleteDialogProduct.id) {
          setQuickViewProduct(null);
        }
      } else {
        showSnackbar("Could not delete product. Please try again.", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("An error occurred while deleting the product.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Add or update product
  const handleSetProduct = async (
    action = "ADD",
    prod_id = null,
    data = productContext.state
  ) => {
    let finalId = prod_id;
    if (!finalId || finalId.trim() === "") {
      const prefix = data.title.trim().toUpperCase().substring(0, 10).replace(/ /g, "_");
      const randomStr = Math.random().toString(36).substring(2, 10).toUpperCase();
      finalId = prefix + "_" + randomStr;
    } else {
      finalId = finalId.trim().replace(/ /g, "_");
    }

    try {
      const res = await productAPI.setProduct({
        id: finalId,
        payload: { ...data, id: finalId },
      });

      if (res.success) {
        const actionMsg = action === "ADD" ? "created" : "updated";
        showSnackbar(`Product ${actionMsg} successfully!`, "success");

        if (action === "ADD") {
          const newProduct = {
            ...data,
            id: finalId,
            createdAt: { seconds: Math.floor(Date.now() / 1000) },
          };
          allProductsContext.setState([newProduct, ...allProductsContext.state]);
        } else {
          const index = allProductsContext.state.findIndex((p) => p.id === finalId);
          if (index !== -1) {
            const copy = [...allProductsContext.state];
            copy[index] = { ...copy[index], ...data };
            allProductsContext.setState(copy);
          }
        }
        productContext.setState(initState);
        hideAddProductModal();
      } else {
        showSnackbar("Error saving product: " + res.message, "error");
      }
    } catch (ex) {
      console.error(ex);
      showSnackbar("Error saving product. Please try again.", "error");
    }
  };

  // Toggle visibility directly
  const handleToggleVisibility = async (row) => {
    const newVisibility = !row.visible;
    try {
      const res = await productAPI.setProduct({
        id: row.id,
        payload: { ...row, visible: newVisibility },
      });
      if (res.success) {
        const index = allProductsContext.state.findIndex((p) => p.id === row.id);
        if (index !== -1) {
          const copy = [...allProductsContext.state];
          copy[index] = { ...copy[index], visible: newVisibility };
          allProductsContext.setState(copy);
        }
        showSnackbar(
          `Product is now ${newVisibility ? "visible on store" : "hidden from store"}.`,
          "info"
        );
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update product visibility.", "error");
    }
  };


  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("ALL");
    setStockFilter("ALL");
    setVisibilityFilter("ALL");
  };

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    selectedCategory !== "ALL" ||
    stockFilter !== "ALL" ||
    visibilityFilter !== "ALL";

  const activeFiltersCount =
    (searchQuery.trim() !== "" ? 1 : 0) +
    (selectedCategory !== "ALL" ? 1 : 0) +
    (stockFilter !== "ALL" ? 1 : 0) +
    (visibilityFilter !== "ALL" ? 1 : 0);

  // DataGrid Columns definition
  const columns = [
    {
      field: "images",
      headerName: "Photo",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const imgs = params.row.images || [];
        const hasImg = imgs.length > 0 && imgs[0].url;
        return (
          <Avatar
            variant="rounded"
            src={hasImg ? imgs[0].url : ""}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <ImageIcon sx={{ fontSize: "1.1rem", color: "#94a3b8" }} />
          </Avatar>
        );
      },
    },
    {
      field: "id",
      headerName: "SKU",
      hide: true,
    },
    {
      field: "title",
      headerName: "Product Title",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.row.title}
        </Typography>
      ),
    },
    {
      field: "parentCategory",
      headerName: "Category",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: "#475569" }}>
          {params.row.parentCategory?.name || "-"}
        </Typography>
      ),
    },
    {
      field: "netQuantity",
      headerName: "Quantity",
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: "#475569" }}>
          {params.row.netQuantity
            ? `${params.row.netQuantity} ${params.row.unitType || ""}`.trim()
            : "-"}
        </Typography>
      ),
    },
    {
      field: "pricing",
      headerName: "Price",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
            {formatINR(params.row.listPrice)}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b", lineHeight: 1.2 }}>
            MRP {formatINR(params.row.maxPrice)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "visible",
      headerName: "Visibility",
      width: 110,
      renderCell: (params) => {
        const isVisible = params.row.visible !== false;
        return (
          <Chip
            label={isVisible ? "Visible" : "Hidden"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleVisibility(params.row);
            }}
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: isVisible ? "#ecfdf5" : "#f1f5f9",
              color: isVisible ? "#047857" : "#64748b",
              border: `1px solid ${isVisible ? "#a7f3d0" : "#cbd5e1"}`,
              cursor: "pointer",
              "&:hover": {
                bgcolor: isVisible ? "#d1fae5" : "#e2e8f0",
              },
            }}
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created at",
      width: 160,
      renderCell: (params) => {
        const t = formatDateTimeForAdmin(params.row.createdAt);
        return (
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
            {t}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero Header */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          color: "#ffffff",
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ zIndex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 0.5,
                borderRadius: "6px",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "inline-flex",
              }}
            >
              <Inventory2OutlinedIcon fontSize="small" />
            </Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.1em",
                color: "#a7f3d0",
                fontSize: "0.75rem",
              }}
            >
              CATALOG & INVENTORY MANAGEMENT
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, fontSize: { xs: "1.75rem", sm: "2.2rem" }, letterSpacing: "-0.02em" }}
          >
            Products
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 520 }}>
            Manage pricing, stock counts, marketplace links, high-res photos, and storefront visibility in real-time.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ zIndex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          <Tooltip title="Refresh Catalog Data">
            <IconButton
              onClick={fetchData}
              disabled={isRefreshing}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#ffffff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <CustomButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => showAddProductModal("ADD")}
            sx={{
              fontWeight: 800,
              bgcolor: "#ffffff",
              color: "#004d40",
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "#f8fafc", color: "#00382e" },
            }}
          >
            Add New Product
          </CustomButton>
        </Stack>
      </Box>

      {/* KPI Summary Cards */}
      <Grid2 container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Products"
            value={products.length}
            note={`${visibleProductsCount} active • ${hiddenProductsCount} hidden • ${healthyStockCount} in stock`}
            icon={<Inventory2OutlinedIcon />}
            onClick={() => {
              setStockFilter("ALL");
              setVisibilityFilter("ALL");
            }}
          />
        </Grid2>

        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Live On Store"
            value={visibleProductsCount}
            note="Visible to customers online"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="#15803d"
            background="#f0fdf4"
            selected={visibilityFilter === "VISIBLE"}
            onClick={() => setVisibilityFilter(visibilityFilter === "VISIBLE" ? "ALL" : "VISIBLE")}
            delay={70}
          />
        </Grid2>

        <Grid2
          xs={6}
          sm={6}
          md={3}
          sx={lowStockCount === 0 ? { display: { xs: "none", sm: "block" } } : undefined}
        >
          <AdminStatCard
            label="Low Stock Alert"
            value={lowStockCount}
            note="1 to 5 units remaining"
            icon={<WarningAmberRoundedIcon />}
            color="#b45309"
            background="#fffbeb"
            selected={stockFilter === "LOW_STOCK"}
            onClick={() => setStockFilter(stockFilter === "LOW_STOCK" ? "ALL" : "LOW_STOCK")}
            delay={140}
          />
        </Grid2>

        <Grid2
          xs={6}
          sm={6}
          md={3}
          sx={outOfStockCount === 0 ? { display: { xs: "none", sm: "block" } } : undefined}
        >
          <AdminStatCard
            label="Out Of Stock"
            value={outOfStockCount}
            note="Needs immediate inventory restock"
            icon={<ErrorOutlineRoundedIcon />}
            color="#b91c1c"
            background="#fef2f2"
            selected={stockFilter === "OUT_OF_STOCK"}
            onClick={() => setStockFilter(stockFilter === "OUT_OF_STOCK" ? "ALL" : "OUT_OF_STOCK")}
            delay={210}
          />
        </Grid2>
      </Grid2>

      {/* Desktop Filter and Search Bar */}
      {!isMobile && (
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              {/* Search Input */}
              <TextField
                size="small"
                placeholder="Search by title, SKU, category, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 },
                }}
              />

              {/* Quick Filters */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                {/* Category Filter */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="category-filter-label">Category</InputLabel>
                  <Select
                    labelId="category-filter-label"
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                  >
                    <MenuItem value="ALL">All Categories</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Stock Filter */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="stock-filter-label">Stock Status</InputLabel>
                  <Select
                    labelId="stock-filter-label"
                    value={stockFilter}
                    label="Stock Status"
                    onChange={(e) => setStockFilter(e.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                  >
                    <MenuItem value="ALL">All Stock</MenuItem>
                    <MenuItem value="IN_STOCK">In Stock (&gt;5)</MenuItem>
                    <MenuItem value="LOW_STOCK">Low Stock (≤5)</MenuItem>
                    <MenuItem value="OUT_OF_STOCK">Out of Stock (0)</MenuItem>
                  </Select>
                </FormControl>

                {/* Visibility Filter */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="vis-filter-label">Visibility</InputLabel>
                  <Select
                    labelId="vis-filter-label"
                    value={visibilityFilter}
                    label="Visibility"
                    onChange={(e) => setVisibilityFilter(e.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value="VISIBLE">Visible Only</MenuItem>
                    <MenuItem value="HIDDEN">Hidden Only</MenuItem>
                  </Select>
                </FormControl>

                {hasActiveFilters && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleResetFilters}
                    sx={{ fontWeight: 700, color: "#64748b" }}
                  >
                    Reset
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Filter Status Bar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                Showing {filteredProducts.length} of {products.length} products • Click any row to edit
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                {selectedCategory !== "ALL" && (
                  <Chip
                    size="small"
                    label={`Category: ${
                      categories.find((c) => c.id === selectedCategory)?.name || selectedCategory
                    }`}
                    onDelete={() => setSelectedCategory("ALL")}
                  />
                )}
                {stockFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={`Stock: ${stockFilter.replace(/_/g, " ")}`}
                    onDelete={() => setStockFilter("ALL")}
                  />
                )}
                {visibilityFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={`Visibility: ${visibilityFilter}`}
                    onDelete={() => setVisibilityFilter("ALL")}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Mobile View: Product Cards, Header, Floating Filter Button & Drawer */}
      {isMobile ? (
        <Box sx={{ pb: 2 }}>
          {/* Mobile Header Bar */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 1.25 }}>
              Products ({filteredProducts.length})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="error">
                    <TuneIcon fontSize="small" />
                  </Badge>
                }
                onClick={() => setMobileFilterOpen(true)}
                sx={{
                  minHeight: 38,
                  borderColor: "#94a3b8",
                  color: "#334155",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  "&:hover": { borderColor: "#004d40", bgcolor: "#f0fdfa" },
                }}
              >
                Filter & Search
              </Button>
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => showAddProductModal("ADD")}
                sx={{
                  bgcolor: "#004d40",
                  color: "#ffffff",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  minHeight: 38,
                  boxShadow: "0 2px 8px rgba(0, 77, 64, 0.2)",
                  "&:hover": { bgcolor: "#00382e" },
                }}
              >
                Add Product
              </Button>
            </Stack>

            {/* Active filter chips on mobile */}
            {hasActiveFilters && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.25 }}
              >
                {searchQuery.trim() && (
                  <Chip
                    size="small"
                    label={`"${searchQuery}"`}
                    onDelete={() => setSearchQuery("")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {selectedCategory !== "ALL" && (
                  <Chip
                    size="small"
                    label={categories.find((c) => c.id === selectedCategory)?.name || "Category"}
                    onDelete={() => setSelectedCategory("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {stockFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={stockFilter.replace(/_/g, " ")}
                    onDelete={() => setStockFilter("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {visibilityFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={visibilityFilter}
                    onDelete={() => setVisibilityFilter("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ minWidth: 0, p: 0.5, fontSize: "0.72rem", fontWeight: 700, color: "#dc2626" }}
                >
                  Clear
                </Button>
              </Stack>
            )}
          </Box>

          {/* Cards List */}
          {filteredProducts.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 3,
                border: "1px dashed #cbd5e1",
                bgcolor: "#f8fafc",
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 44, color: "#94a3b8", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                No products found
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, mb: 2 }}>
                Try adjusting your search query or removing active filters.
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Clear all filters
                </Button>
              )}
            </Paper>
          ) : (
            filteredProducts.map((product) => {
              const imgs = product.images || [];
              const hasImg = imgs.length > 0 && imgs[0].url;
              const isVisible = product.visible !== false;
              const units = Number(product.unitsInStock) || 0;

              return (
                <Paper
                  key={product.id}
                  elevation={0}
                  onClick={() => setEditProduct(product)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:active": {
                      transform: "scale(0.99)",
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                      variant="rounded"
                      src={hasImg ? imgs[0].url : ""}
                      alt={product.title}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2.5,
                        border: "1px solid #f1f5f9",
                        bgcolor: "#f8fafc",
                        flexShrink: 0,
                      }}
                    >
                      <ImageIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                        {product.parentCategory?.name && (
                          <Chip
                            label={product.parentCategory.name}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: "#f1f5f9",
                              color: "#475569",
                            }}
                          />
                        )}
                        {product.netQuantity && (
                          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                            {product.netQuantity} {product.unitType || ""}
                          </Typography>
                        )}
                      </Stack>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: "#0f172a",
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {product.title}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Price and Stock row */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid #f1f5f9" }}
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        {formatINR(product.listPrice)}
                      </Typography>
                      {Number(product.maxPrice) > Number(product.listPrice) && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#94a3b8", textDecoration: "line-through" }}
                        >
                          MRP {formatINR(product.maxPrice)}
                        </Typography>
                      )}
                    </Stack>

                    {units === 0 ? (
                      <Chip
                        size="small"
                        label="Out of Stock"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#fef2f2",
                          color: "#dc2626",
                          border: "1px solid #fecaca",
                        }}
                      />
                    ) : units <= 5 ? (
                      <Chip
                        size="small"
                        label={`${units} left`}
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#fffbeb",
                          color: "#b45309",
                          border: "1px solid #fde68a",
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={`${units} in stock`}
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "#f0fdf4",
                          color: "#15803d",
                          border: "1px solid #bbf7d0",
                        }}
                      />
                    )}
                  </Stack>

                  {/* Footer Controls: Visibility & Actions */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1.25 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Chip
                      label={isVisible ? "Visible" : "Hidden"}
                      size="small"
                      onClick={() => handleToggleVisibility(product)}
                      sx={{
                        height: 24,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        bgcolor: isVisible ? "#ecfdf5" : "#f1f5f9",
                        color: isVisible ? "#047857" : "#64748b",
                        border: `1px solid ${isVisible ? "#a7f3d0" : "#cbd5e1"}`,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: isVisible ? "#d1fae5" : "#e2e8f0",
                        },
                      }}
                    />

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Quick Preview">
                        <IconButton
                          size="small"
                          onClick={() => setQuickViewProduct(product)}
                          sx={{
                            color: "#64748b",
                            bgcolor: "#f8fafc",
                            "&:hover": { bgcolor: "#f1f5f9" },
                          }}
                        >
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Product">
                        <IconButton
                          size="small"
                          onClick={() => setEditProduct(product)}
                          sx={{
                            color: "#004d40",
                            bgcolor: "#f0fdf4",
                            "&:hover": { bgcolor: "#dcfce7" },
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Product">
                        <IconButton
                          size="small"
                          onClick={() => confirmDeleteProduct(product)}
                          sx={{
                            color: "#dc2626",
                            bgcolor: "#fef2f2",
                            "&:hover": { bgcolor: "#fee2e2" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })
          )}

          {/* Mobile Filter & Search Drawer */}
          <Drawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                maxHeight: "88vh",
                p: 2.5,
                pb: 3.5,
                bgcolor: "#f8fafc",
                border: "1px solid #dbe7e4",
                borderBottom: 0,
                background: "linear-gradient(180deg, #ecfdf8 0%, #ffffff 22%, #f8fafc 58%)",
                boxShadow: "0 -14px 42px rgba(15, 23, 42, 0.18)",
              },
            }}
            ModalProps={{ keepMounted: true, hideBackdrop: true }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: "#cbd5e1",
                mx: "auto",
                mb: 2,
              }}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Filter & Search
                </Typography>
                {activeFiltersCount > 0 && (
                  <Chip
                    size="small"
                    label={`${activeFiltersCount} Active`}
                    sx={{
                      bgcolor: "#004d40",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                )}
              </Stack>
              <IconButton size="small" onClick={() => setMobileFilterOpen(false)}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}
              >
                SEARCH PRODUCTS
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by title, SKU, category, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", borderRadius: 2 },
                }}
              />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}
              >
                CATEGORY
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#ffffff" }}
                >
                  <MenuItem value="ALL">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}
              >
                STOCK STATUS
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#ffffff" }}
                >
                  <MenuItem value="ALL">All Stock</MenuItem>
                  <MenuItem value="IN_STOCK">In Stock (&gt;5)</MenuItem>
                  <MenuItem value="LOW_STOCK">Low Stock (≤5)</MenuItem>
                  <MenuItem value="OUT_OF_STOCK">Out of Stock (0)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#64748b", mb: 0.75, display: "block" }}
              >
                VISIBILITY
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#ffffff" }}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="VISIBLE">Visible Only</MenuItem>
                  <MenuItem value="HIDDEN">Hidden Only</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1.5}>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleResetFilters}
                  sx={{
                    flex: 1,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: "none",
                    borderColor: "#cbd5e1",
                    color: "#64748b",
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => setMobileFilterOpen(false)}
                sx={{
                  flex: hasActiveFilters ? 2 : 1,
                  borderRadius: 2.5,
                  bgcolor: "#004d40",
                  color: "#ffffff",
                  fontWeight: 800,
                  textTransform: "none",
                  py: 1.2,
                  boxShadow: "0 4px 14px rgba(0, 77, 64, 0.25)",
                  "&:hover": { bgcolor: "#00382e" },
                }}
              >
                Show {filteredProducts.length} Product{filteredProducts.length === 1 ? "" : "s"}
              </Button>
            </Stack>
          </Drawer>
        </Box>
      ) : (
        /* Main Table for Desktop */
        <CustomDataGrid
          rows={filteredProducts}
          columns={columns}
          styles={{ height: "620px" }}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          pageSizes={[50, 100]}
          onRowClickHandle={(params) => setEditProduct(params.row)}
        />
      )}

      {/* Add / Edit Product Modal */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openProductModal}
        onClose={hideAddProductModal}
        closeAfterTransition
      >
        <Fade in={openProductModal}>
          <Box sx={styles.boxStyle}>
            <AddProduct
              action={editProductModal ? "EDIT" : "ADD"}
              handleSetProduct={handleSetProduct}
              hideModal={hideAddProductModal}
              onDeleteProduct={(p) => confirmDeleteProduct(p)}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Quick View Product Modal */}
      <Dialog
        open={Boolean(quickViewProduct)}
        onClose={() => setQuickViewProduct(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {quickViewProduct && (
          <>
            <DialogTitle sx={{ fontWeight: 800, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Product Quick Preview
              </Typography>
              <IconButton size="small" onClick={() => setQuickViewProduct(null)}>
                <ClearIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 2 }}>
              <Stack spacing={2.5}>
                {/* Images row */}
                {(quickViewProduct.images || []).length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1 }}>
                    {quickViewProduct.images.map((img, idx) => (
                      <Avatar
                        key={idx}
                        variant="rounded"
                        src={img.url}
                        sx={{ width: 100, height: 100, borderRadius: 2, border: "1px solid #e2e8f0" }}
                      />
                    ))}
                  </Box>
                )}

                <Box>
                  <Chip
                    label={`SKU: ${quickViewProduct.id}`}
                    size="small"
                    sx={{ fontFamily: "monospace", fontWeight: 700, mb: 1 }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                    {quickViewProduct.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                    {quickViewProduct.parentCategory?.name || "General"} • {quickViewProduct.netQuantity}{" "}
                    {quickViewProduct.unitType}
                  </Typography>
                </Box>

                <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Grid2 container spacing={2}>
                    <Grid2 xs={4}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Selling Price
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#004d40" }}>
                        {formatINR(quickViewProduct.listPrice)}
                      </Typography>
                    </Grid2>
                    <Grid2 xs={4}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Printed MRP
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#64748b" }}>
                        {formatINR(quickViewProduct.maxPrice)}
                      </Typography>
                    </Grid2>
                    <Grid2 xs={4}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Inventory
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          color: Number(quickViewProduct.unitsInStock) === 0 ? "#ef4444" : "#10b981",
                        }}
                      >
                        {quickViewProduct.unitsInStock} units
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Paper>

                {/* Marketplace URLs */}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {quickViewProduct.amazonURL && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      href={quickViewProduct.amazonURL}
                      target="_blank"
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Amazon Listing
                    </Button>
                  )}
                  {quickViewProduct.flipkartURL && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      href={quickViewProduct.flipkartURL}
                      target="_blank"
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Flipkart Listing
                    </Button>
                  )}
                  {quickViewProduct.tata1mgURL && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                      href={quickViewProduct.tata1mgURL}
                      target="_blank"
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Tata 1mg Listing
                    </Button>
                  )}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  const p = quickViewProduct;
                  setQuickViewProduct(null);
                  confirmDeleteProduct(p);
                }}
              >
                Delete
              </Button>
              <Stack direction="row" spacing={1}>
                <Button onClick={() => setQuickViewProduct(null)}>Close</Button>
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => {
                    const p = quickViewProduct;
                    setQuickViewProduct(null);
                    setEditProduct(p);
                  }}
                  sx={{ fontWeight: 700 }}
                >
                  Edit Product
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDialogProduct)}
        onClose={() => !isDeleting && setDeleteDialogProduct(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#b91c1c" }}>
          Delete Product Catalog Item?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#334155" }}>
            Are you sure you want to permanently delete{" "}
            <strong>"{deleteDialogProduct?.title}"</strong> (SKU: {deleteDialogProduct?.id})?
            <br />
            <br />
            This item will be removed from your active database and cannot be recovered.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogProduct(null)}
            disabled={isDeleting}
            sx={{ fontWeight: 700, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExecuteDelete}
            disabled={isDeleting}
            sx={{ fontWeight: 800 }}
          >
            {isDeleting ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={3500}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={hideSnackbar} severity={message.type} sx={{ width: "100%", borderRadius: 2 }}>
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsListAdmin;
